import { useState, useEffect } from "react"
import { useProjects } from "../contexts/ProjectContext"
import { Globe, Trash2, Eye, Plus, Loader2 } from 'lucide-react'
import { useNavigate } from "react-router-dom"
import api from "../utils/apiClient"
import AddCompetitorModal from "../components/AddCompetitorModal"

const Competitors = () => {
  const navigate = useNavigate()
  const { currentProject, loadCurrentProject } = useProjects()
  const [competitors, setCompetitors] = useState([])
  const [loading, setLoading] = useState(false)
  const [deletingIds, setDeletingIds] = useState([])
  const [error, setError] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [failedFavicons, setFailedFavicons] = useState(new Set())

  useEffect(() => {
    if (currentProject?.project_competitors) {
      setCompetitors(currentProject.project_competitors)
    }
  }, [currentProject])

  const handleAddCompetitor = async (newCompetitorUrl) => {
    setLoading(true)
    setError("")

    try {
      // Make the API call first
      const updatedUrls = [...competitors.map((c) => c.url), newCompetitorUrl]
      const response = await api.projects.setCompetitors(currentProject.id, updatedUrls)

      // Only update the UI after the API call succeeds
      // The response.data contains the updated list of competitors with their IDs
      if (response.data && Array.isArray(response.data)) {
        setCompetitors(response.data)

        // Find the newly added competitor from the response
        const newCompetitorData = response.data.find(
          c => c.url === newCompetitorUrl
        )

        if (newCompetitorData?.id) {
          // Auto-fetch sitemaps for the new competitor
          await api.competitors.autoFetchSitemaps(newCompetitorData.id)
        }
      } else {
        // Fallback if response format is unexpected
        const newCompetitor = { url: newCompetitorUrl }
        setCompetitors(prev => [...prev, newCompetitor])
      }

      // Close the modal
      setIsModalOpen(false)

      // Reload the project to get the new competitor with its ID and ensure global state is synced
      // We need to tell loadCurrentProject not to redirect to onboarding AND force a reload
      await loadCurrentProject(currentProject.id, { preventRedirect: true, force: true })
    } catch (err) {
      console.error("Error adding competitor:", err)
      setError(err.response?.data?.error || err.message || "Failed to add competitor")
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteCompetitor = async (competitorUrl) => {
    // Add the URL to deletingIds to show loading state
    setDeletingIds((prev) => [...prev, competitorUrl])
    setError("")

    try {
      // Create a new array with filtered competitors
      const updatedCompetitors = competitors.filter((c) => c.url !== competitorUrl)

      // Make the API call with the updated URLs
      const updatedUrls = updatedCompetitors.map((c) => c.url)
      await api.projects.setCompetitors(currentProject.id, updatedUrls)

      // Only update local state after the API call succeeds
      setCompetitors(updatedCompetitors)

      // Don't call loadCurrentProject here to avoid race conditions
    } catch (err) {
      console.error("Error deleting competitor:", err)
      setError(err.response?.data?.error || err.message || "Failed to delete competitor")
    } finally {
      // Remove the URL from deletingIds
      setDeletingIds((prev) => prev.filter((id) => id !== competitorUrl))
    }
  }

  const isDeleting = (url) => deletingIds.includes(url)

  const getFaviconUrl = (url) => {
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`)
      const domain = urlObj.hostname.replace('www.', '')
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`
    } catch {
      return null
    }
  }

  const handleFaviconError = (url) => {
    setFailedFavicons(prev => new Set([...prev, url]))
  }

  return (
    <div className="flex-1 min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="xl:px-8 2xl:px-12">
        <div className="py-6">
          {/* Header Section */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-1">Competitors</h1>
                <p className="text-sm text-gray-600">Track and analyze your competitors' content strategies</p>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Competitor
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                </div>
                <div className="ml-3">
                  <p className="text-red-800 text-sm font-medium">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Competitors Grid */}
          <div className="space-y-3">
            {competitors.map((competitor) => (
              <div
                key={competitor.url}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all duration-200 group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      <div className="p-2 bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg group-hover:from-primary-100 group-hover:to-primary-200 transition-all duration-200">
                        {!failedFavicons.has(competitor.url) && getFaviconUrl(competitor.url) ? (
                          <img
                            src={getFaviconUrl(competitor.url)}
                            alt={`${competitor.url} favicon`}
                            className="w-5 h-5 rounded"
                            onError={() => handleFaviconError(competitor.url)}
                          />
                        ) : (
                          <Globe className="w-5 h-5 text-primary-600" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 truncate">{competitor.url}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Competitor website</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 ml-3">
                    <button
                      onClick={() => navigate(`/dashboard/competitor/${encodeURIComponent(competitor.url)}`)}
                      className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                      title="View Details"
                      disabled={isDeleting(competitor.url)}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCompetitor(competitor.url)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 flex items-center justify-center"
                      title="Delete Competitor"
                      disabled={isDeleting(competitor.url)}
                    >
                      {isDeleting(competitor.url) ? (
                        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Empty State */}
            {competitors.length === 0 && (
              <div className="text-center py-12">
                <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-8 max-w-md mx-auto">
                  <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg w-fit mx-auto mb-4">
                    <Globe className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">No competitors added</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Add your first competitor to start tracking their content and strategies
                  </p>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 text-sm font-medium disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />
                    Add Competitor
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AddCompetitorModal
        isOpen={isModalOpen}
        onClose={() => !loading && setIsModalOpen(false)}
        onAdd={handleAddCompetitor}
        loading={loading}
      />
    </div>
  )
}

export default Competitors
